import Axios from "axios";

const state = () => ({
  currentImage: null,
  images: [],
  categories: [],
  info: {},
  code: null,
  message: null,
  status: null,
  loading: false,
  uploading: false,
  host: process.env.VUE_APP_API_HOST || 'http://localhost:8020',
});

const mutations = {
  set_current_image(state, image) {
    state.currentImage = image;
  },
  set_images(state, images) {
    state.images = images;
  },
  set_categories(state, categories) {
    state.categories = categories;
  },
  set_info(state, info) {
    state.info = info;
  },
  set_code(state, code) {
    state.code = code;
  },
  set_message(state, message) {
    state.message = message;
  },
  set_status(state, status) {
    state.status = status;
  },
  set_loading(state, loading) {
    state.loading = loading;
  },
  set_uploading(state, uploading) {
    state.uploading = uploading;
  },
  clear_status(state) {
    state.code = null;
    state.message = null;
    state.status = null;
  }
};

const actions = {
  /**
   * Fetch image by ID
   */
  fetchImageById({ commit, state }, imageId) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/${imageId}`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.get(url, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_current_image", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Image fetched successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to fetch image");
          }
        })
        .catch((error) => {
          commit("set_current_image", null);
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Fetch all images
   */
  fetchAllImages({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.get(url, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_images", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Images fetched successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to fetch images");
          }
        })
        .catch((error) => {
          commit("set_images", []);
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Fetch images by category
   */
  fetchImagesByCategory({ commit, state }, categoryId) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/category/${categoryId}/images`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.get(url, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_images", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Images fetched successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to fetch images");
          }
        })
        .catch((error) => {
          commit("set_images", []);
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Upload new image
   */
  uploadImage({ commit, state }, payload) {
    return new Promise((resolve, reject) => {
      commit("set_uploading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/upload`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "multipart/form-data"
      };

      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("label", payload.label);
      formData.append("categoryId", payload.categoryId);
      
      if (payload.displayOrder) {
        formData.append("displayOrder", payload.displayOrder);
      }
      
      if (payload.altText) {
        formData.append("altText", payload.altText);
      }

      Axios.post(url, formData, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_current_image", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Image uploaded successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to upload image");
          }
        })
        .catch((error) => {
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_uploading", false);
        });
    });
  },

  /**
   * Update existing image
   */
  updateImage({ commit, state }, payload) {
    return new Promise((resolve, reject) => {
      commit("set_uploading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/${payload.id}`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "multipart/form-data"
      };

      const formData = new FormData();
      
      // File is optional for update
      if (payload.file) {
        formData.append("file", payload.file);
      }
      
      formData.append("label", payload.label);
      formData.append("categoryId", payload.categoryId);
      
      if (payload.displayOrder) {
        formData.append("displayOrder", payload.displayOrder);
      }
      
      if (payload.altText) {
        formData.append("altText", payload.altText);
      }

      Axios.put(url, formData, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_current_image", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Image updated successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to update image");
          }
        })
        .catch((error) => {
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_uploading", false);
        });
    });
  },

  /**
   * Delete image
   */
  deleteImage({ commit, state }, imageId) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/${imageId}`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.delete(url, { headers })
        .then((response) => {
          const data = response.data;

          commit("set_current_image", null);
          commit("set_code", response.status);
          commit("set_message", data.message || "Image deleted successfully");
          commit("set_status", "success");
          resolve(data);
        })
        .catch((error) => {
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Search images by label
   */
  searchImages({ commit, state }, label) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/search`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.get(url, { 
        headers,
        params: { label } 
      })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_images", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Images found");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to search images");
          }
        })
        .catch((error) => {
          commit("set_images", []);
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Fetch all categories
   */
  fetchCategories({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit("set_loading", true);
      commit("clear_status");

      const url = `${state.host}/api/tool/dynamic-image/category`;
      const headers = { 
        Authorization: "Bearer " + localStorage.getItem("token") 
      };

      Axios.get(url, { headers })
        .then((response) => {
          const data = response.data;

          if (data.success && data.data) {
            commit("set_categories", data.data);
            commit("set_code", response.status);
            commit("set_message", data.message || "Categories fetched successfully");
            commit("set_status", "success");
            resolve(data.data);
          } else {
            throw new Error(data.message || "Failed to fetch categories");
          }
        })
        .catch((error) => {
          commit("set_categories", []);
          commit("set_code", error.response ? error.response.status : 500);
          commit("set_message", error.response?.data?.message || error.message);
          commit("set_status", "error");
          reject(error);
        })
        .finally(() => {
          commit("set_loading", false);
        });
    });
  },

  /**
   * Get image URL for viewing
   */
  getImageUrl({ state }, imageId) {
    return `${state.host}/api/tool/dynamic-image/view/${imageId}`;
  },

  /**
   * Clear current image
   */
  clearCurrentImage({ commit }) {
    commit("set_current_image", null);
  },

  /**
   * Clear all state
   */
  clearState({ commit }) {
    commit("set_current_image", null);
    commit("set_images", []);
    commit("set_categories", []);
    commit("clear_status");
    commit("set_loading", false);
    commit("set_uploading", false);
  }
};

const getters = {
  currentImage: state => state.currentImage,
  images: state => state.images,
  categories: state => state.categories,
  isLoading: state => state.loading,
  isUploading: state => state.uploading,
  status: state => state.status,
  message: state => state.message,
  code: state => state.code,
  hasError: state => state.status === "error",
  hasSuccess: state => state.status === "success",
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
